import { Box, CircularProgress, Typography, Backdrop } from "@mui/material";

interface LoadingSpinnerProps {
  /** Size of the spinner in pixels */
  size?: number;
  /** Optional message to display below spinner */
  message?: string;
  /** Color of the spinner */
  color?: "primary" | "secondary" | "inherit";
}

/**
 * Loading Spinner Component
 * Simple circular progress indicator for inline loading states
 */
export function LoadingSpinner({
  size = 40,
  message,
  color = "primary",
}: LoadingSpinnerProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <CircularProgress size={size} color={color} />
      {message && (
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      )}
    </Box>
  );
}

interface LoadingOverlayProps {
  /** Whether the overlay is visible */
  open: boolean;
  /** Optional message to display */
  message?: string;
}

/**
 * Loading Overlay Component
 * Full-screen overlay with loading spinner for blocking operations
 */
export function LoadingOverlay({ open, message }: LoadingOverlayProps) {
  return (
    <Backdrop
      sx={{
        color: "#fff",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        flexDirection: "column",
        gap: 2,
      }}
      open={open}
    >
      <CircularProgress color="inherit" size={48} />
      {message && (
        <Typography variant="body1" color="inherit">
          {message}
        </Typography>
      )}
    </Backdrop>
  );
}

interface LoadingPageProps {
  /** Optional message to display */
  message?: string;
}

/**
 * Loading Page Component
 * Full-page loading state for Suspense fallback and initial page loads
 */
export function LoadingPage({ message = "Loading..." }: LoadingPageProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        gap: 3,
      }}
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      <CircularProgress size={48} color="primary" />
      <Typography variant="body1" color="text.secondary">
        {message}
      </Typography>
    </Box>
  );
}

/**
 * Inline Loading Component
 * Small loading indicator for inline loading states (buttons, form fields)
 */
export function InlineLoading() {
  return (
    <CircularProgress
      size={20}
      color="inherit"
      sx={{ ml: 1 }}
      aria-label="Loading"
    />
  );
}

export default LoadingSpinner;
